import { Alert, Box, Divider, Typography, useTheme } from '@mui/material';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Settings } from '@frontend/features/agencies/components/Settings';
import { SettingsFields } from '@bandido/shared/types';
import { useGetSettingsByAgencyId } from '@frontend/features/agencies/queries';
import { CopilotConfigsList } from '@frontend/features/copilot/components/CopilotConfigsList';
import { TravelIntelligenceSettings } from '@frontend/features/copilot/components/TravelIntelligenceSettings';

export const CopilotView = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: settings, isLoading: settingsLoading } =
    useGetSettingsByAgencyId({ id });

  // La API key nunca se muestra: si ya hay una guardada, ocultamos su valor
  // (campo vacío) y avisamos que solo hay que completarlo para cambiarla.
  const hasApiKey = Boolean(settings?.[SettingsFields.COPILOT_API_KEY]);
  const displayValues = Object.fromEntries(
    Object.entries(settings ?? {}).filter(
      ([key]) => !(hasApiKey && key === SettingsFields.COPILOT_API_KEY),
    ),
  );

  return (
    <Box
      sx={{
        borderRadius: '24px',
        backgroundColor: theme.palette.common.white,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        height: 'calc(100vh - 180px)',
        overflow: 'hidden',
        padding: theme.spacing(1, 3),
      }}
    >
      <OverlayScrollbarsComponent
        style={{
          marginTop: theme.spacing(5),
          boxSizing: 'border-box',
          width: '100%',
          overflowY: 'hidden',
          height: 'calc(100vh - 160px)',
        }}
        className="overlayscrollbars-react"
        options={{ scrollbars: { autoHide: 'leave', theme: 'os-theme-light' } }}
        defer
      >
        <Box display="flex" flexDirection="column" gap={5}>
          <Box>
            <Typography variant="h5">{t('copilot.settingsTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('copilot.pageIntro')}
            </Typography>
          </Box>

          {hasApiKey && (
            <Alert severity="info">{t('copilot.apiKeyConfigured')}</Alert>
          )}

          <Settings
            title={t('copilot.credentialsTitle')}
            agencyId={Number(id)}
            fields={[
              SettingsFields.COPILOT_AGENCY_CONTEXT,
              SettingsFields.COPILOT_PROVIDER,
              SettingsFields.COPILOT_PASSIVE_MODEL,
              SettingsFields.COPILOT_ACTIVE_MODEL,
              SettingsFields.COPILOT_API_KEY,
            ]}
            values={displayValues}
            options={{
              fieldTypes: [
                {
                  id: SettingsFields.COPILOT_AGENCY_CONTEXT,
                  type: 'textfield',
                  multiline: true,
                  minRows: 4,
                },
                {
                  id: SettingsFields.COPILOT_PROVIDER,
                  type: 'select',
                  options: [{ label: 'OpenAI', value: 'openai' }],
                },
                { id: SettingsFields.COPILOT_API_KEY, type: 'password' },
              ],
            }}
          />

          <Divider />
          <TravelIntelligenceSettings
            agencyId={Number(id)}
            settings={settings}
            isLoading={settingsLoading}
          />

          <Divider />
          <CopilotConfigsList agencyId={Number(id)} />
        </Box>
      </OverlayScrollbarsComponent>
    </Box>
  );
};

export default CopilotView;
